<template>
  <section class="section-graphs loader-wrapper" ref="callGraphsContainer">
    <div class="graph graph-doughnut">
      <div class="graph-i">
        <div class="chart-titles">
          <div class="chart-title">
            <span class="chart-title-t">{{ $t("Phones.TotalCalls") }}</span>
            <span class="chart-title-b text-grey">{{ totalCallsCount }}</span>
          </div>
        </div>
        <div class="chart-list">
          <div class="chart-item">
            <div class="chart-container">
              <PhonesGraphDoughnut :width="144" :height="144" :chart-data="answeredCallsChartData" :options="chartOptionsDoughnut"
                    ref="answeredCallsGraph" />
              <div class="chart-val">{{ numberOfAnsweredCalls }}</div>
            </div>
            <div class="chart-labels">
              <div class="chart-label">
                <span class="color has-background-success"></span>
                <span class="txt">{{ $t("Phones.Grid.Columns.AnsweredCalls") }}</span>
              </div>
            </div>
          </div>
          <div class="chart-item">
            <div class="chart-container">
              <PhonesGraphDoughnut :width="144" :height="144" :chart-data="notAnsweredCallsChartData" :options="chartOptionsDoughnut"
                    ref="notAnsweredCallsGraph" />
              <div class="chart-val">{{ numberOfMissedCalls }}</div>
            </div>
            <div class="chart-labels">
              <div class="chart-label">
                <span class="color has-background-danger"></span>
                <span class="txt">{{ $t("Phones.Grid.Columns.MissedCalls") }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="graph graph-bar">
      <div class="graph-i">
        <div class="chart-item">
          <div class="chart-titles">
            <div class="chart-title">
              <span class="chart-title-t">{{ $t("Phones.TotalMinutes") }}</span>
              <span class="chart-title-b text-grey">{{ totalCallsMinutes }}</span>
            </div>
            <div v-if="phoneStatTruncated">
              {{ $t("Phones.GraphMaxDaysRestriction", { MAX_DAYS: MAX_STAT_ITEMS_FOR_GRAPH }) }}
            </div>
          </div>
          <mq-layout class="chart-container" :mq="['sm', 'md']">
            <div class="chart-wrapper">
              <div :style="{width: (leadsStatisticCallsByDateChartDataMobile.datasets[0].data.length * 150) + 'px'}">
                <PhonesGraphBar :height="200" :chart-data="leadsStatisticCallsByDateChartDataMobile" ref="leadsStatisticCallsByDateGraphMobile" />
              </div>
            </div>
          </mq-layout>
          <mq-layout class="chart-container" mq="lg">
            <div class="chart-wrapper">
              <div :style="{width: (leadsStatisticCallsByDateChartData.datasets[0].data.length * 150) + 'px'}">
                <PhonesGraphBar :height="200" :chart-data="leadsStatisticCallsByDateChartData" ref="leadsStatisticCallsByDateGraph" />
              </div>
            </div>
          </mq-layout>

          <div class="chart-labels">
            <div class="chart-label">
              <span class="color has-background-warning"></span>
              <span class="txt">{{ $t("Phones.DurationOfCallInMinutes") }}</span>
            </div>
            <div class="chart-label">
              <span class="color has-background-primary"></span>
              <span class="txt">{{ $t("Phones.NumberOfCalls") }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { ChartData, ChartOptions } from "chart.js";
import moment from "moment";
import { LoaderComponent } from "vue-loading-overlay";
import VueMq from "vue-mq";
import { Component, Vue, Watch } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import i18n from "@/i18n";
import { DataSourceResult } from "@/models/DataSourceResult";
import { LeadStatisticCallsByDate, PhoneLeadStats, PhoneStat } from "@/models/leads/LeadStatistic";
import HelperService from "@/services/HelperService";
import PhoneService from "@/services/PhoneService";
import PhonesGraphBar from "./PhonesGraphBar.vue";
import PhonesGraphDoughnut from "./PhonesGraphDoughnut.vue";
import IconBase from "../IconBase.vue";
import IconChartPie from "../icons/IconChartPie.vue";
import IconChartLine from "../icons/IconChartLine.vue";
import IconCalendarTime from "../icons/IconCalendarTime.vue";
import IconQuestionCircle from "../icons/IconQuestionCircle.vue";

Vue.use(VueMq, {
  breakpoints: {
    sm: 576,
    md: 992,
    lg: Infinity,
  },
  defaultBreakpoint: "sm",
});
const namespace = "phonesModule";
const leadsNamespace = "leadsModule";

@Component({
  components: {
    IconBase,
    IconChartPie,
    IconChartLine,
    IconCalendarTime,
    IconQuestionCircle,
    PhonesGraphBar,
    PhonesGraphDoughnut,
  },
})
export default class PhonesGraphs extends Vue {

  //#region Vuex getters and actions

  @Getter("leadsStatisticPhones", { namespace: leadsNamespace }) leadsStatisticPhones!: PhoneLeadStats;
  @Getter("phones", { namespace }) phones!: DataSourceResult;
  @Action("setPhoneLeadStats", { namespace }) setPhoneLeadStats!: (phoneLeadStats: PhoneLeadStats) => void;
  @Action("setPhones", { namespace }) setPhones!: (phones: DataSourceResult) => void;
  
  //#endregion Vuex getters and actions


  //#region Properties

  MAX_STAT_ITEMS_FOR_GRAPH = 365;
  numberOfAnsweredCalls = 0;
  numberOfMissedCalls = 0;
  phoneStatTruncated = false;

  chartOptionsDoughnut: ChartOptions = {
    legend: {
      display: false,
    },
    layout: {
      padding: 0,
    },
    tooltips: {
      enabled: false,
    },
    cutoutPercentage: 83,
    aspectRatio: 1,
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 0,
    },
    responsiveAnimationDuration: 0,
    plugins: {
      datalabels: {
        display: false,
      },
    },
  };

  answeredCallsChartData: ChartData = {
    datasets: [
      {
        backgroundColor: ["#269339", "#e9eaf0"],
        data: [0, 0],
      },
    ],
    labels: [i18n.t("Phones.Grid.Columns.AnsweredCalls").toString(), ""],
  };

  notAnsweredCallsChartData: ChartData = {
    datasets: [
      {
        backgroundColor: ["#DE473C", "#e9eaf0"],
        data: [0, 0],
      },
    ],
    labels: [i18n.t("Phones.Grid.Columns.MissedCalls").toString(), ""],
  };

  leadsStatisticCallsByDateChartData: ChartData = {
    datasets: [
      {
        label: i18n.t("Phones.NumberOfCalls").toString(),
        backgroundColor: "#0093D0",
        barPercentage: .9,
        categoryPercentage: .8,
        data: [],
      },
      {
        label: i18n.t("Phones.DurationOfCallInMinutes").toString(),
        backgroundColor: "#FF9724",
        barPercentage: .9,
        categoryPercentage: .8,
        data: [],
      },
    ],
  };

  leadsStatisticCallsByDateChartDataMobile: ChartData = {
    datasets: [
      {
        label: i18n.t("Phones.NumberOfCalls").toString(),
        backgroundColor: "#0093D0",
        barPercentage: .9,
        categoryPercentage: .8,
        data: [],
      },
      {
        label: i18n.t("Phones.DurationOfCallInMinutes").toString(),
        backgroundColor: "#FF9724",
        barPercentage: .9,
        categoryPercentage: .8,
        data: [],
      },
    ],
  };

  loader!: LoaderComponent;
  
  //#endregion Properties


  //#region Watchers

  @Watch("leadsStatisticPhones", { immediate: true })
  onLeadsStatisticCallsByDateChange(newValue: PhoneLeadStats, oldValue: PhoneLeadStats): void {
    if (oldValue === undefined || !newValue) {
      if (this.loader) {
        this.loader.hide();
      }
      if (this.$refs.callGraphsContainer) {
        this.loader = this.$loading.show({
          container: this.$refs.callGraphsContainer
        });
      }
      return;
    } else {
      const leadsStatisticCallsByDateReduced: LeadStatisticCallsByDate[] = newValue.CallsStatByDate.slice(0, this.MAX_STAT_ITEMS_FOR_GRAPH);
      this.phoneStatTruncated = leadsStatisticCallsByDateReduced.length < newValue.CallsStatByDate.length;
      this.leadsStatisticCallsByDateChartData.labels = this.leadsStatisticCallsByDateChartDataMobile.labels =
            leadsStatisticCallsByDateReduced.map((i: LeadStatisticCallsByDate) => moment(i.Date).format("DD/MM/YYYY"));
      if (this.leadsStatisticCallsByDateChartData!.datasets && this.leadsStatisticCallsByDateChartDataMobile!.datasets) {
        this.leadsStatisticCallsByDateChartData.datasets[0].data = this.leadsStatisticCallsByDateChartDataMobile.datasets[0].data =
              leadsStatisticCallsByDateReduced.map((i: LeadStatisticCallsByDate) => i.NumberOfCalls);
        this.leadsStatisticCallsByDateChartData.datasets[1].data = this.leadsStatisticCallsByDateChartDataMobile.datasets[1].data =
              leadsStatisticCallsByDateReduced.map((i: LeadStatisticCallsByDate) => {
          return HelperService.convertSecondsToMinutes(i.DurationOfCalls);
        });
      }
      if (this.$refs.leadsStatisticCallsByDateGraph) {
        (this.$refs.leadsStatisticCallsByDateGraph as any).refreshChart();
      } else if (this.$refs.leadsStatisticCallsByDateGraphMobile) {
        (this.$refs.leadsStatisticCallsByDateGraphMobile as any).refreshChart();
      }

      this.numberOfAnsweredCalls = newValue.CallsStatByDate.map((i: LeadStatisticCallsByDate) => i.NumberOfAnsweredCalls)
            .reduce((partialSum: number, current: number) => partialSum + current, 0);
      this.numberOfMissedCalls = newValue.CallsStatByDate.map((i: LeadStatisticCallsByDate) => i.NumberOfMissedCalls)
            .reduce((partialSum: number, current: number) => partialSum + current, 0);
      if (this.answeredCallsChartData!.datasets) {
        this.answeredCallsChartData.datasets[0].data = [this.numberOfAnsweredCalls, this.totalCallsCount - this.numberOfAnsweredCalls];
      }
      if (this.notAnsweredCallsChartData!.datasets) {
        this.notAnsweredCallsChartData.datasets[0].data = [this.numberOfMissedCalls, this.totalCallsCount - this.numberOfMissedCalls];
      }
      (this.$refs.answeredCallsGraph as any).refreshChart();
      (this.$refs.notAnsweredCallsGraph as any).refreshChart();

      if (this.phones?.Data) {
        const newPhones: DataSourceResult = PhoneService.setStatsForPhones(this.phones, newValue);
        this.setPhones(newPhones);
      }
      this.setPhoneLeadStats(newValue);
      this.loader.hide();
    }
  }

  //#endregion Watchers


  //#region Getters

  get totalCallsCount(): number {
    return this.numberOfAnsweredCalls + this.numberOfMissedCalls;
  }

  get totalCallsMinutes(): number {
    if (!this.leadsStatisticPhones) {
      return 0;
    }
    const seconds = this.leadsStatisticPhones?.CallsStatByDate.map((i: LeadStatisticCallsByDate) => i.DurationOfCalls)
          .reduce((partialSum: number, current: number) => partialSum + current, 0);
    return HelperService.convertSecondsToMinutes(seconds);
  }

  //#endregion Getters


  //#region Private methods

  

  //#endregion Private methods
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";

.section-graphs {
  margin: 0 -12px 30px;
  display: flex;
  align-items: stretch;
  @media (max-width: 1279px) {
    display: block;
    margin: 0 0 30px;
  }
  @media (max-width: 991px) {
    margin-bottom: 20px;
  }
  .graph {
    display: flex;
    align-items: stretch;
    padding: 0 12px;
    @media (max-width: 1279px) {
      padding: 0;
    }
  }
  .graph-i {
    background: #fff;
    border: 1px solid #d3d6e2;
    width: 100%;
    border-radius: 4px;
    padding: 10px 24px;
    @media (max-width: 1599px) {
      padding: 10px 16px;
    }
  }

  .chart-titles {
    display: flex;
    justify-content: space-between;

    .chart-title {
      padding: 0 0 5px 5px;
      font-size: 17px;
      font-weight: 600;
      min-height: 60px;
      .chart-title-t {
        display: flex;
        align-items: center;
        .b-tooltip {
          margin: 0 5px 0 0;
          color: #435c65;
          .icon-chart-pie {
            width: 14px;
            height: 14px;
          }
        }
        .tooltip-content {
          max-width: 160px;
        }
      }
      .chart-title-b {
        display: block;
        &.text-grey {
          color: #aaa;
        }
      }
    }
  }

  .graph {
    &.graph-doughnut {
      width: 30%;
      flex: 0 0 30%;
      display: flex;
      align-items: stretch;
      @media (max-width: 1279px) {
        width: 100%;
        margin-bottom: 20px;
      }
      .chart-list {
        width: 100%;
        display: flex;
        align-items: stretch;
        flex-wrap: wrap;
        margin: 0 -1px -1px 0;
        @media (max-width: 479px) {
          display: block;
        }
        .chart-item {
          width: 50%;
          flex: 0 0 50%;
          @media (max-width: 479px) {
            width: 100%;
          }
          &:not(:last-child) {
            @media (max-width: 479px) {
              margin-bottom: 30px;
            }
          }
        }
        .chart-container {
          width: 144px;
          height: 144px;
          position: relative;
          justify-content: center;
          margin: 0 auto 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          @media (max-width: 479px) {
            margin: 0 auto 10px;
          }
          .chart-val {
            position: absolute;
            font-size: 24px;
            font-weight: 600;
            text-align: center;
          }
        }

        .chart-labels {
          .chart-label {
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 10px 5px;
            .color {
              width: 6px;
              height: 6px;
              flex: 0 0 6px;
              margin: 2px 0 0 5px;
            }
            .txt {
              font-size: 14px;
              font-weight: 600;
            }
          }
        }
      }
    }
    &.graph-bar {
      width: 70%;
      flex: 0 0 70%;
      @media (max-width: 1279px) {
        width: 100%;
      }
      .chart-item {
      }
      .chart-labels {
        display: flex;
        align-items: center;
        justify-content: center;
        .chart-label {
          display: flex;
          align-items: center;
          margin: 0 10px 5px;
          .color {
            width: 6px;
            height: 6px;
            flex: 0 0 6px;
            margin-top: 2px;
            margin-inline-end: 9px;
          }
          .txt {
            font-size: 14px;
            font-weight: 600;
            flex: 1;
          }
        }
        hr {
          margin: 20px 0 18px;
          background: #d3d6e2;
          height: 1px;
        }
      }
      .chart-container {
        flex: 1;
        width: 100%;
        height: 200px;
        margin-bottom: 10px;

        .chart-wrapper {
          overflow-x: auto;
          width: 100%;
        }
      }
    }
  }
}

html[dir="ltr"] {
  .section-graphs {
    .graph {
      &.graph-doughnut {
        .chart-list {
          .chart-labels {
            .chart-label {
              .color {
                margin: 2px 5px 0 0;
              }
            }
          }
        }
      }
    }
  }
}
</style>

<style lang="scss">
.graphs-roi .b-tooltip.is-dark .tooltip-content {
  width: 500px;
  font-weight: normal;
  font-size: 16px;
  background: #303b41;
  padding: 15px 18px 18px;
  @media (max-width: 1199px) {
    width: auto;
    min-width: 280px;
  }
  @media (max-width: 479px) {
    left: -40px;
    transform: none;
  }
  &:before {
    border-width: 7px;
    @media (max-width: 479px) {
      left: 48px;
    }
  }
}
html[dir="ltr"] {
  .graphs-roi .b-tooltip.is-dark .tooltip-content {
    @media (max-width: 479px) {
      left: auto;
      right: -40px;
    }
    &:before {
      @media (max-width: 479px) {
        right: 34px;
        left: auto;
      }
    }
  }
}
</style>
